import React, { useState } from 'react';
import { clsx } from 'clsx';
import { vars } from '@ag1/ui/theme';
import { outer, inner } from './FlavorBubbleButton.css';

export const flavors = [
  'ag1-original',
  'ag1-tropical',
  'ag1-citrus',
  'ag1-berry',
  'agz-original',
  'agz-chocolateMint',
  'agz-chocolate',
  'agz-mixedBerry'
] as const;

export type Flavor = (typeof flavors)[number];

type State = 'default' | 'hover' | 'active';

interface FlavorBubbleProps {
  flavor: Flavor;
  activeColor: string;
  isActive?: boolean;
  onClick?: () => void;
  className?: string;
  'aria-label'?: string;
}

const FlavorBubbleButton: React.FC<FlavorBubbleProps> = ({
  flavor,
  isActive = false,
  onClick,
  className,
  activeColor = 'primaryInverse',
  ...rest
}) => {
  const [isHovered, setHovered] = useState(false);
  const [isPressed, setPressed] = useState(false);

  let state: State = 'default';
  if (isActive || isPressed) {
    state = 'active';
  } else if (isHovered) {
    state = 'hover';
  }

  let dynamicBoxShadow = 'none';

  if (state === 'active') {
    dynamicBoxShadow = `0 0 0 1px ${
      vars.colors.text[activeColor as keyof typeof vars.colors.text]
    }`;
  } else if (state === 'hover') {
    dynamicBoxShadow = `0 0 0 1px ${vars.colors.button.flavor.hoverBorder}`;
  }

  return (
    <div className={outer} style={{ boxShadow: dynamicBoxShadow }}>
      <div
        role="button"
        tabIndex={0}
        className={clsx(inner({ flavor }), className)}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => {
          setHovered(false);
          setPressed(false);
        }}
        onMouseDown={() => setPressed(true)}
        onMouseUp={() => setPressed(false)}
        onFocus={() => setHovered(true)}
        onBlur={() => {
          setHovered(false);
          setPressed(false);
        }}
        onClick={onClick}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onClick?.();
            setPressed(true);
          }
        }}
        onKeyUp={() => setPressed(false)}
        {...rest}
      />
    </div>
  );
};

export default FlavorBubbleButton;
